// Thin wrapper around raw PeerJS mechanics — connecting to the public
// broker, dialing/accepting DataConnections, and classifying its errors.
// No knowledge of the game protocol (ClientMessage/ServerMessage/Room)
// lives here; see hostRoom.ts and client.ts for that.

import Peer, { type DataConnection, PeerErrorType } from 'peerjs';

export function openHostPeer(id: string): Promise<Peer> {
  return new Promise((resolve, reject) => {
    const peer = new Peer(id);
    peer.once('open', () => resolve(peer));
    peer.once('error', (err) => reject(err));
  });
}

export function openClientPeer(): Promise<Peer> {
  return new Promise((resolve, reject) => {
    const peer = new Peer();
    peer.once('open', () => resolve(peer));
    peer.once('error', (err) => reject(err));
  });
}

export function dial(peer: Peer, hostId: string, label: 'ctrl' | 'path', reliable: boolean): DataConnection {
  return peer.connect(hostId, { label, reliable, serialization: 'json' });
}

export function waitForOpen(conn: DataConnection, timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    if (conn.open) { resolve(); return; }
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('timed out waiting for connection to open'));
    }, timeoutMs);
    const cleanup = () => {
      clearTimeout(timer);
      conn.off('open', onOpen);
      conn.off('close', onFail);
      conn.off('error', onFail);
    };
    const onOpen = () => { cleanup(); resolve(); };
    const onFail = (err?: unknown) => { cleanup(); reject(err instanceof Error ? err : new Error('connection failed')); };
    conn.once('open', onOpen);
    conn.once('close', onFail);
    conn.once('error', onFail);
  });
}

// PeerJS's DataConnection only emits 'close'/'error' on a graceful
// shutdown — an abruptly killed remote peer (tab closed, crashed, network
// dropped) never fires either, so a connection can go dead with no signal
// at all. The underlying RTCPeerConnection does notice: its connectionState
// moves through 'disconnected' to 'failed' within a few seconds. Watching
// that (in addition to the DataConnection's own events) is what actually
// lets us detect the other side vanishing. We only act on 'failed'/'closed',
// not 'disconnected', since 'disconnected' can be a brief, recoverable blip.
export function watchConnectionHealth(conn: DataConnection, onLost: () => void): void {
  let fired = false;
  const fire = () => {
    if (fired) return;
    fired = true;
    onLost();
  };
  conn.on('close', fire);
  conn.on('error', fire);
  conn.peerConnection.addEventListener('connectionstatechange', () => {
    const state = conn.peerConnection.connectionState;
    if (state === 'failed' || state === 'closed') fire();
  });
}

export function isUnavailableId(err: unknown): boolean {
  return (err as { type?: string } | undefined)?.type === PeerErrorType.UnavailableID;
}

export function isPeerUnavailable(err: unknown): boolean {
  return (err as { type?: string } | undefined)?.type === PeerErrorType.PeerUnavailable;
}
